import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayOf, node, object, shape, string } from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { NavLink } from "react-router-dom";

const PageSubMenu = ({
  children,
  id,
  links,
  location,
  matchDepth,
  trackClick,
  ...rest
}) => {
  const [open, setOpen] = useState(false);

  const toggle = useRef();
  const dropdown = useRef();
  const { pathname } = location || {};

  const handleToggle = useCallback(() => setOpen(!open), [open]);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (
        toggle.current !== document.activeElement &&
        !!dropdown.current &&
        !dropdown.current.contains(document.activeElement) &&
        open
      ) {
        handleToggle();
      }
    }, 50);
  }, [handleToggle, open]);

  const toggleClass = useMemo(() => {
    if (!pathname || !links || !links.length) {
      return "";
    }

    return links.some(({ path }) => pathname.includes(path)) ? "active" : "";
  }, [links, pathname]);

  useEffect(() => {
    document.addEventListener("click", handleBlur);

    return () => {
      document.removeEventListener("click", handleBlur);
    };
  }, [handleBlur]);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  useEffect(() => {
    if (open && !!dropdown.current) {
      dropdown.current.firstChild.focus();
    }
  }, [open]);

  return !!links && !!links.length ? (
    <Group {...rest}>
      <Toggle
        aria-expanded={open}
        className={toggleClass}
        id={id}
        onClick={handleToggle}
        ref={toggle}
      >
        {children}
        <Icon icon={open ? faAngleUp : faAngleDown} />
      </Toggle>
      {!!links && !!links.length && (
        <Dropdown aria-labelledby={id} open={open} ref={dropdown}>
          {links
            .filter(link => !!link)
            .map(({ path, title }) =>
              path.startsWith("https://") ? (
                <DropdownUrl
                  key={path}
                  href={path}
                  target={"_blank"}
                  rel="noreferrer"
                  onClick={e => {
                    trackClick(e, title.toLowerCase());
                  }}
                >
                  {title}
                </DropdownUrl>
              ) : (
                <SubMenuLink
                  key={path}
                  matchDepth={matchDepth}
                  onBlur={handleBlur}
                  to={path}
                >
                  {title}
                </SubMenuLink>
              )
            )}
        </Dropdown>
      )}
    </Group>
  ) : null;
};

PageSubMenu.propTypes = {
  children: node.isRequired,
  id: string,
  links: arrayOf(
    shape({
      path: string,
      title: string,
    })
  ),
  location: object,
  matchDepth: string,
};

PageSubMenu.defaultProps = {
  id: "submenu-toggle",
};

const SubMenuLink = ({ matchDepth, to, ...rest }) => {
  const checkIfActive = useCallback(
    (match, location) => {
      try {
        const { pathname, search } = location;
        const isExact = !!match && match.isExact;

        switch (matchDepth) {
          case "path":
            return isExact || pathname.includes(to);
          case "search":
            return to === pathname + search;
          default:
            return isExact;
        }
      } catch (err) {
        return false;
      }
    },
    [matchDepth, to]
  );

  return <DropdownLink {...rest} isActive={checkIfActive} to={to} />;
};

const Group = styled.div`
  position: relative;
  z-index: 1500;
`;

const Toggle = styled.button.attrs(props => ({
  "aria-haspopup": "true",
  className: "submenu-toggle",
  type: "button",
}))`
  appearance: none;
  display: flex;
  line-height: 1;
  margin: 0;
  user-select: none;
  white-space: nowrap;

  &:focus {
    outline: ${rem("1px")} dotted;
    outline-offset: ${rem("1px")};
  }
`;

const Icon = styled(FontAwesomeIcon).attrs(props => ({
  className: "submenu-icon",
}))`
  margin-left: ${rem("3px")};
`;

const Dropdown = styled.div.attrs(props => ({
  className: "submenu-list",
  tabIndex: "-1",
}))`
  display: ${({ open }) => (open ? "block" : "none")};
  list-style: none;
  margin: 0;
  padding: 0;
`;

const DropdownLink = styled(NavLink).attrs(props => ({
  className: "submenu-link",
  tabIndex: "0",
}))`
  display: block;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
`;

const DropdownUrl = styled.a.attrs(props => ({
  className: "submenu-url",
  tabIndex: "0",
}))`
  display: block;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
`;

export default PageSubMenu;
